import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import axios from "axios";
import Layout from "../components/custom/Layout";
import OctaPanel from "../components/custom/OctaPanel";
import { API } from "../contexts/AuthContext";

function cleanSym(s) {
  if (!s) return s;
  for (const suffix of ["USDT", "PERP", ".NS", ".BSE", ".L", "-USD"]) {
    if (s.toUpperCase().endsWith(suffix)) return s.slice(0, -suffix.length);
  }
  return s;
}

export default function Risk() {
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          axios.get(`${API}/dashboard/positions`),
          axios.get(`${API}/dashboard/summary`),
        ]);
        setPositions(pRes.data);
        setSummary(sRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const totalEquity = summary?.total_equity || 0;
  const totalPnl = summary?.total_pnl || 0;
  const marginUsage = summary?.margin_usage || 0;

  const platformExposure = positions.reduce((acc, pos) => {
    const platform = pos.platform_name || "Unknown";
    const value = (pos.quantity || 0) * (pos.entry_price || 0);
    acc[platform] = (acc[platform] || 0) + value;
    return acc;
  }, {});

  const assetExposure = positions.reduce((acc, pos) => {
    const symbol = cleanSym(pos.symbol) || "?";
    const value = (pos.quantity || 0) * (pos.mark_price || 0);
    acc[symbol] = (acc[symbol] || 0) + value;
    return acc;
  }, {});

  const COLORS = ["#2563EB", "#38BDF8", "#22C55E", "#F59E0B", "#EF4444"];

  const platformData = Object.entries(platformExposure).map(([name, value], i) => ({
    name, value: Math.round(value), fill: COLORS[i % COLORS.length]
  }));
  const assetData = Object.entries(assetExposure).map(([name, value], i) => ({
    name, value: Math.round(value), fill: COLORS[i % COLORS.length]
  }));

  const marginLevel = marginUsage > 80 ? "danger" : marginUsage > 50 ? "warning" : "safe";
  const marginConfig = { safe: "#22C55E", warning: "#F59E0B", danger: "#EF4444" };

  const riskMetrics = [
    { label: "Total Equity", value: `$${totalEquity.toLocaleString("en-US", { minimumFractionDigits: 2 })}`, color: "#E2E8F0", sub: "Portfolio value" },
    { label: "Unrealized PnL", value: `${totalPnl >= 0 ? "+" : ""}$${totalPnl.toFixed(2)}`, color: totalPnl >= 0 ? "#22C55E" : "#EF4444", sub: "Open positions" },
    { label: "Margin Usage", value: `${marginUsage.toFixed(1)}%`, color: marginConfig[marginLevel], sub: marginLevel.toUpperCase() },
    { label: "Open Positions", value: positions.length.toString(), color: "#38BDF8", sub: "Active trades" },
  ];

  return (
    <Layout summary={summary}>
      <div className="p-7" data-testid="risk-page">
        <div className="mb-7">
          <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", fontSize: "16px", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Risk Overview
          </h1>
          <p style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", color: "#475569", marginTop: "3px", fontWeight: "500", letterSpacing: "0.08em" }}>
            Portfolio risk metrics and exposure analysis
          </p>
        </div>

        {/* Risk Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-7">
          {riskMetrics.map((m, i) => (
            <OctaPanel key={m.label} delay={i * 0.07} className="p-5">
              <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.14em", color: "#475569", fontWeight: "500", textTransform: "uppercase", marginBottom: "6px" }}>{m.label}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "20px", fontWeight: "700", color: m.color, fontVariantNumeric: "tabular-nums" }} data-testid={`risk-metric-${m.label.toLowerCase().replace(/ /g, "-")}`}>
                {m.value}
              </div>
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#475569", marginTop: "4px" }}>{m.sub}</div>
            </OctaPanel>
          ))}
        </div>

        {/* Margin Usage Bar */}
        <OctaPanel delay={0.2} className="p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="flex items-center gap-2" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px" }}>
              <ShieldAlert size={14} /> Margin Utilization
            </h3>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", fontWeight: "600", color: marginConfig[marginLevel], fontVariantNumeric: "tabular-nums" }}>
              {marginUsage.toFixed(1)}%
            </span>
          </div>
          <div className="h-1.5 octa-clip-sm overflow-hidden" style={{ background: "rgba(255,255,255,0.03)" }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${marginUsage}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full"
              style={{ background: marginConfig[marginLevel] }}
            />
          </div>
          {marginLevel === "danger" && (
            <div className="flex items-center gap-2 mt-3" style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#EF4444" }}>
              <AlertTriangle size={12} /> High margin usage — consider reducing exposure
            </div>
          )}
        </OctaPanel>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <OctaPanel delay={0.25} className="p-5">
            <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px", marginBottom: "16px" }}>Exposure by Platform</h3>
            {platformData.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={platformData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                      {platformData.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} opacity={0.8} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#111318", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: "11px" }}
                      labelStyle={{ color: "#E2E8F0" }}
                      itemStyle={{ color: "#94A3B8" }}
                      formatter={(v) => [`$${v.toLocaleString()}`, "Value"]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-3">
                  {platformData.map((p) => (
                    <div key={p.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
                        <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#94A3B8" }}>{p.name}</span>
                      </div>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>
                        ${p.value.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px", color: "#475569", textAlign: "center", padding: "32px 0" }}>No exposure data</p>}
          </OctaPanel>

          <OctaPanel delay={0.3} className="p-5">
            <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px", marginBottom: "16px" }}>Exposure by Asset</h3>
            {assetData.length > 0 ? (
              <div className="space-y-4">
                {assetData.map((a) => {
                  const total = assetData.reduce((s, x) => s + x.value, 0);
                  const pct = total > 0 ? (a.value / total * 100).toFixed(1) : 0;
                  return (
                    <div key={a.name} data-testid={`asset-exposure-${a.name}`}>
                      <div className="flex justify-between mb-1.5">
                        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", color: "#E2E8F0" }}>{a.name}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#94A3B8", fontVariantNumeric: "tabular-nums" }}>
                          ${a.value.toLocaleString()} ({pct}%)
                        </span>
                      </div>
                      <div className="h-1 overflow-hidden" style={{ background: "rgba(255,255,255,0.03)" }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.8, delay: 0.1 }}
                          className="h-full"
                          style={{ background: a.fill }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px", color: "#475569", textAlign: "center", padding: "32px 0" }}>No asset data</p>}
          </OctaPanel>
        </div>

        {/* Position Risk Table */}
        <OctaPanel delay={0.35} className="p-5">
          <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px", marginBottom: "16px" }}>Position Risk Details</h3>
          {positions.length === 0 ? (
            <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px", color: "#475569", textAlign: "center", padding: "32px 0" }}>No open positions</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="risk-positions-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    {["Symbol", "Side", "Size", "Entry", "Mark", "Leverage", "PnL", "Liq. Price"].map(h => (
                      <th key={h} className="text-left pb-3 pr-4" style={{
                        fontFamily: "'Inter Tight', sans-serif",
                        fontSize: "9px",
                        letterSpacing: "0.14em",
                        color: "#334155",
                        fontWeight: "600",
                        textTransform: "uppercase",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positions.map((pos) => (
                    <tr key={pos._id} style={{ borderBottom: "1px solid rgba(255,255,255,0.02)" }} className="hover:bg-white/[0.02]">
                      <td className="py-3 pr-4" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px" }}>{cleanSym(pos.symbol)}</td>
                      <td className="py-3 pr-4" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", color: pos.side === "LONG" ? "#22C55E" : "#EF4444" }}>{pos.side}</td>
                      <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#94A3B8", fontVariantNumeric: "tabular-nums" }}>{pos.quantity}</td>
                      <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#94A3B8", fontVariantNumeric: "tabular-nums" }}>${pos.entry_price?.toLocaleString()}</td>
                      <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>${pos.mark_price?.toLocaleString()}</td>
                      <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#38BDF8", fontVariantNumeric: "tabular-nums" }}>{pos.leverage}x</td>
                      <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", fontWeight: "600", color: pos.pnl >= 0 ? "#22C55E" : "#EF4444", fontVariantNumeric: "tabular-nums" }}>
                        {pos.pnl >= 0 ? "+" : ""}{pos.pnl?.toFixed(2)}
                      </td>
                      <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#F59E0B", fontVariantNumeric: "tabular-nums" }}>${pos.liquidation_price?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </OctaPanel>
      </div>
    </Layout>
  );
}
